/**
 * Client-side tracking-param extraction, shared by the landing lead forms.
 *
 * Every form passes the same UTM + click-id params to its submit action. They
 * used to each inline the identical `searchParams.get('utm_x') ?? undefined`
 * block (and two of them an identical `_fbp`/`_fbc` cookie loop). These pure
 * helpers are the one place that mapping lives.
 *
 * `buildTrackingParams` takes a getter (`searchParams.get` bound, or a
 * window.location reader) so it works for both `useSearchParams` forms and the
 * fallback form that reads `window.location.search` directly. It returns ONLY
 * the 7 params every form sends — cookies + experiment variant stay the
 * caller's concern (not every form reads them), so adopting this never changes
 * what a given form submits.
 */

export interface TrackingParams {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  fbclid?: string;
  gclid?: string;
}

/** Map a param getter to the shared UTM + click-id fields. */
export function buildTrackingParams(
  get: (key: string) => string | null | undefined,
): TrackingParams {
  const v = (key: string) => get(key) ?? undefined;
  return {
    utmSource: v('utm_source'),
    utmMedium: v('utm_medium'),
    utmCampaign: v('utm_campaign'),
    utmContent: v('utm_content'),
    utmTerm: v('utm_term'),
    fbclid: v('fbclid'),
    gclid: v('gclid'),
  };
}

/**
 * Read the Meta first-party `_fbp` / `_fbc` cookies for CAPI attribution.
 * Client-only — returns `{}` during SSR (no `document`).
 */
export function readFbCookies(): { fbp?: string; fbc?: string } {
  let fbp: string | undefined;
  let fbc: string | undefined;
  if (typeof document !== 'undefined') {
    for (const cookie of document.cookie.split('; ')) {
      if (cookie.startsWith('_fbp=')) fbp = cookie.slice(5);
      else if (cookie.startsWith('_fbc=')) fbc = cookie.slice(5);
    }
  }
  return { fbp, fbc };
}
